import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import TierBadge from "@/components/tier-badge";
import { toast } from "sonner";
import { Copy, WhatsappLogo, Gift, UsersThree, TrendUp, Sparkle, PaperPlaneTilt, GraduationCap } from "@phosphor-icons/react";
import { formatDateTime } from "@/lib/date";

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useLang();
  const [stats, setStats] = useState(null);
  const [refs, setRefs] = useState([]);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/referrals").then((r) => setRefs(r.data.slice(0, 5))).catch(() => {});
  }, []);

  const link = `${window.location.origin}/ref/${user?.ref_code || ""}`;
  const remaining = stats ? Math.max(0, 10 - stats.qualified_referrals) : 0;
  const untilNext = stats && stats.next_reward ? 1 : 0;

  const copyLink = async () => {
    await navigator.clipboard.writeText(link);
    toast.success(t.dashboard.copiedToClipboard);
  };

  const waShare = () => {
    const msg = encodeURIComponent(`Hey! Considering your smile? My personal DentOrdu link for a free consultation: ${link}`);
    window.open(`https://wa.me/?text=${msg}`, "_blank");
  };

  const nextRewardLabel = stats?.next_reward ? (t.brand === "DentOrdu" ? stats.next_reward.label_en : stats.next_reward.label_tr) : "—";

  return (
    <div className="space-y-8">
      <div className="fade-up">
        <div className="text-xs uppercase tracking-[0.2em] text-[#e07a5f]">{t.brand} · {t.programme}</div>
        <h1 className="font-serif-display text-4xl md:text-5xl mt-3">{t.dashboard.welcome}, {user?.name}.</h1>
        {stats && (
          <p className="text-[#3b4b5a] mt-2">
            <span className="font-medium text-[#0d3b66]">{untilNext}</span> {t.dashboard.awayFromNext}.
          </p>
        )}
      </div>

      {/* Bento */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 fade-up fade-up-1">
        {/* Progress hero card */}
        <div className="md:col-span-2 md:row-span-2 rounded-3xl bg-[#0d3b66] text-white p-8 relative overflow-hidden">
          <div className="text-xs uppercase tracking-widest text-white/60">{t.dashboard.currentLevel}</div>
          <div className="mt-3"><TierBadge level={stats?.level || "Starter"} size="lg" /></div>
          <div className="mt-8 text-white/70 text-sm">{t.dashboard.nextReward}</div>
          <div className="font-serif-display text-3xl md:text-4xl mt-2 text-[#f4a261] leading-tight">{nextRewardLabel}</div>
          <div className="mt-8">
            <div className="flex items-center justify-between text-xs text-white/70">
              <span>{stats?.qualified_referrals || 0} {t.dashboard.progress}</span>
              <span>{stats?.progress_pct || 0}%</span>
            </div>
            <div className="mt-2 h-2 rounded-full bg-white/15 overflow-hidden">
              <div className="fillbar h-full rounded-full bg-[#f4a261]" style={{ width: `${stats?.progress_pct || 0}%` }} />
            </div>
          </div>
          <div className="absolute -bottom-10 -right-10 w-52 h-52 rounded-full bg-[#f4a261]/10 blur-2xl" />
        </div>

        <Stat icon={UsersThree} label={t.dashboard.totalFriends} value={stats?.total_friends ?? "—"} testid="stat-total-friends" />
        <Stat icon={TrendUp} label={t.dashboard.activeReferrals} value={stats?.active_referrals ?? "—"} testid="stat-active-referrals" />
        <Stat icon={Sparkle} label={t.dashboard.qualifiedReferrals} value={stats?.qualified_referrals ?? "—"} testid="stat-qualified-referrals" />
        <Stat icon={Gift} label={t.dashboard.rewardsEarned} value={stats?.rewards_earned_count ?? "—"} testid="stat-rewards-earned" />
      </div>

      {/* Referral link + Quick actions */}
      <div className="grid md:grid-cols-3 gap-4 fade-up fade-up-2">
        <div className="md:col-span-2 rounded-3xl bg-white border border-[#e6dfd2] p-6">
          <div className="text-xs uppercase tracking-widest text-[#6b7280]">Your personal link</div>
          <div className="mt-3 flex flex-col md:flex-row md:items-center gap-3">
            <code data-testid="referral-link" className="flex-1 px-4 py-3 rounded-full bg-[#f6f2ea] border border-[#e6dfd2] text-sm truncate">{link}</code>
            <div className="flex gap-2">
              <button data-testid="copy-link-btn" onClick={copyLink} className="inline-flex items-center gap-2 px-4 py-3 rounded-full bg-[#0d3b66] text-[#f4a261] text-sm hover:bg-[#071f36]">
                <Copy size={14} /> {t.dashboard.copyLink}
              </button>
              <button data-testid="share-whatsapp-btn" onClick={waShare} className="inline-flex items-center gap-2 px-4 py-3 rounded-full bg-[#25D366] text-white text-sm hover:opacity-90">
                <WhatsappLogo size={16} weight="fill" /> {t.dashboard.shareWhatsapp}
              </button>
            </div>
          </div>
        </div>
        <div className="rounded-3xl bg-white border border-[#e6dfd2] p-6 flex flex-col justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#6b7280]">{t.dashboard.quickActions}</div>
            <div className="mt-3 space-y-2">
              <Link data-testid="qa-invite-btn" to="/app/friends" className="flex items-center justify-between px-4 py-2.5 rounded-full border border-[#e6dfd2] text-sm hover:border-[#0d3b66]">
                <span className="flex items-center gap-2"><PaperPlaneTilt size={16} weight="duotone" /> {t.dashboard.inviteFriend}</span>
                <span>→</span>
              </Link>
              <Link data-testid="qa-toolkit-btn" to="/app/academy" className="flex items-center justify-between px-4 py-2.5 rounded-full border border-[#e6dfd2] text-sm hover:border-[#0d3b66]">
                <span className="flex items-center gap-2"><GraduationCap size={16} weight="duotone" /> {t.dashboard.sharingKit}</span>
                <span>→</span>
              </Link>
              <Link data-testid="qa-rewards-btn" to="/app/rewards" className="flex items-center justify-between px-4 py-2.5 rounded-full border border-[#e6dfd2] text-sm hover:border-[#0d3b66]">
                <span className="flex items-center gap-2"><Gift size={16} weight="duotone" /> {t.dashboard.viewRewards}</span>
                <span>→</span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Friends list */}
      <div className="rounded-3xl bg-white border border-[#e6dfd2] p-6 fade-up fade-up-3">
        <div className="flex items-center justify-between">
          <h2 className="font-serif-display text-2xl">{t.dashboard.recentFriends}</h2>
          <Link data-testid="dashboard-friends-view-all" to="/app/friends" className="text-sm text-[#0d3b66] hover:underline">{t.dashboard.viewAll} →</Link>
        </div>
        <div className="mt-4 divide-y divide-[#e6dfd2]">
          {refs.length === 0 && <div className="py-8 text-center text-sm text-[#6b7280]">{t.friends.empty}</div>}
          {refs.map((r) => (
            <div key={r.id} className="py-3 flex items-center justify-between row-hover -mx-2 px-2 rounded-lg">
              <div>
                <div className="font-medium">{r.display_name}</div>
                <div className="text-xs text-[#6b7280]">{formatDateTime(r.created_at)}</div>
              </div>
              <div className="text-xs px-3 py-1 rounded-full bg-[#f6f2ea] border border-[#e6dfd2]">{t.statuses[r.status]}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div data-testid={testid} className="rounded-3xl bg-white border border-[#e6dfd2] p-5 card-lift">
      <div className="w-10 h-10 rounded-full bg-[#f6f2ea] flex items-center justify-center">
        <Icon size={20} weight="duotone" color="#0d3b66" />
      </div>
      <div className="text-xs uppercase tracking-widest text-[#6b7280] mt-4">{label}</div>
      <div className="font-serif-display text-3xl mt-1">{value}</div>
    </div>
  );
}
